import React, { useState, useEffect } from "react";
import { useStores } from "../stores";
import { apiPostPut } from "../api/api_methods";
import { rolesWithId } from "../constants/constants";
import Loader from "./reusableComponents/loader";

function Profile() {
  const { clientsStore } = useStores();
  const [loading, setLoading] = useState(false);
  const [isEditing, setIsEditing] = useState(false);
  const [editFormData, setEditFormData] = useState({});
  const [message, setMessage] = useState("");

  useEffect(() => {
    // Initialize edit form with current user data
    if (clientsStore.user) {
      setEditFormData({
        name: clientsStore.user.name || "",
        email: clientsStore.user.email || "",
        mobile:
          clientsStore.user.mobileNumber || clientsStore.user.mobile || "",
        // Add other fields as needed
      });
    }
  }, [clientsStore.user]);

  const handleEdit = () => {
    setIsEditing(true);
  };

  const handleSave = async () => {
    try {
      setLoading(true);
      const user = JSON.parse(localStorage.getItem("user"));

      const response = await apiPostPut(
        {
          ...editFormData,
          userId: user._id,
        },
        `/users/${user._id}`,
        "PUT"
      );

      if (response.status === 200 && response.body.success) {
        // Update the user data in store and localStorage
        const updatedUser = { ...clientsStore.user, ...editFormData };
        clientsStore.setUserData(updatedUser);
        setMessage("Profile updated successfully!");
        setIsEditing(false);
      } else {
        setMessage(response.body.message || "Failed to update profile");
      }
    } catch (error) {
      console.error("Error updating profile:", error);
      setMessage("Error updating profile");
    } finally {
      setLoading(false);
    }
  };

  const handleCancel = () => {
    setIsEditing(false);
    // Reset form data to original values
    if (clientsStore.user) {
      setEditFormData({
        name: clientsStore.user.name || "",
        email: clientsStore.user.email || "",
        mobile:
          clientsStore.user.mobileNumber || clientsStore.user.mobile || "",
      });
    }
    setMessage("");
  };

  const getRoleDisplayName = (roleId) => {
    if (roleId === rolesWithId.admin) return "Admin";
    if (roleId === rolesWithId.agent) return "Financial Planner";
    return "User";
  };

  if (!clientsStore.user) {
    return (
      <div className="p-6 bg-white rounded-lg shadow-md">
        <div className="text-center py-8 text-gray-500">Loading profile...</div>
      </div>
    );
  }

  return (
    <div className="p-6 bg-white rounded-lg shadow-md">
      <h2 className="text-2xl font-bold mb-6 text-gray-800">Profile</h2>

      {message && (
        <div
          className={`mb-4 p-3 rounded ${
            message.includes("successfully")
              ? "bg-green-100 text-green-700 border border-green-300"
              : "bg-red-100 text-red-700 border border-red-300"
          }`}
        >
          {message}
        </div>
      )}

      <div className="max-w-2xl">
        <div className="bg-gray-50 rounded-lg p-6 mb-6">
          <h3 className="text-lg font-semibold mb-4 text-gray-700">
            Account Information
          </h3>

          <div className="grid grid-cols-1 md:grid-cols-2 gap-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Role
              </label>
              <p className="text-gray-900 font-medium">
                {getRoleDisplayName(clientsStore.user.role)}
              </p>
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                User ID
              </label>
              <p className="text-gray-900 font-mono text-sm">
                {clientsStore.user._id}
              </p>
            </div>
          </div>
        </div>

        <div className="bg-white border border-gray-200 rounded-lg p-6">
          <div className="flex justify-between items-center mb-4">
            <h3 className="text-lg font-semibold text-gray-700">
              Personal Information
            </h3>
            {!isEditing && (
              <button
                onClick={handleEdit}
                className="px-4 py-2 bg-blue-600 text-white rounded-md hover:bg-blue-700"
              >
                Edit Profile
              </button>
            )}
          </div>

          <div className="space-y-4">
            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Name
              </label>
              {isEditing ? (
                <input
                  type="text"
                  value={editFormData.name}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, name: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="text-gray-900">{clientsStore.user.name}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Email
              </label>
              {isEditing ? (
                <input
                  type="email"
                  value={editFormData.email}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, email: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="text-gray-900">{clientsStore.user.email}</p>
              )}
            </div>

            <div>
              <label className="block text-sm font-medium text-gray-600 mb-1">
                Mobile Number
              </label>
              {isEditing ? (
                <input
                  type="tel"
                  value={editFormData.mobile}
                  onChange={(e) =>
                    setEditFormData({ ...editFormData, mobile: e.target.value })
                  }
                  className="w-full px-3 py-2 border border-gray-300 rounded-md focus:outline-none focus:ring-2 focus:ring-blue-500"
                />
              ) : (
                <p className="text-gray-900">
                  {clientsStore.user.mobileNumber ||
                    clientsStore.user.mobile ||
                    "Not provided"}
                </p>
              )}
            </div>

            {isEditing && (
              <div className="flex gap-3 pt-4">
                <button
                  onClick={handleSave}
                  disabled={loading}
                  className="px-4 py-2 bg-green-600 text-white rounded-md hover:bg-green-700 disabled:opacity-50"
                >
                  {loading ? "Saving..." : "Save Changes"}
                </button>
                <button
                  onClick={handleCancel}
                  disabled={loading}
                  className="px-4 py-2 bg-gray-600 text-white rounded-md hover:bg-gray-700 disabled:opacity-50"
                >
                  Cancel
                </button>
              </div>
            )}
          </div>
        </div>
      </div>

      <Loader loader={loading} text="Updating profile..." />
    </div>
  );
}

export default Profile;
